// components/SoftSkillVendorDialog.tsx
import AsyncMultiSelectField from "@/components/form/components/AsyncMultiSelectField";
import InputField from "@/components/form/components/InputField";
import SelectField from "@/components/form/components/SelectField";
import TextareaField from "@/components/form/components/Textarea";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Form } from "@/components/ui/form";
import {
  useCreateSoftSkillVendorMutation,
  useUpdateSoftSkillVendorMutation,
} from "@/hooks/api/softSkillVendor.queries";
import { departmentService } from "@/services/department.service";
import {
  SoftSkillVendorCreateType,
  SoftSkillVendorParamsType,
  SoftSkillVendorType,
  softSkillVendorService,
} from "@/services/softSkillVendor.service";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import * as z from "zod";

type SoftSkillVendorDialogProps = {
  mode: "create" | "edit";
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  filters: SoftSkillVendorParamsType;
  vendorId?: string;
  vendorData?: SoftSkillVendorType | null;
  onCancel: () => void;
};

const STATUS_OPTIONS = [
  { label: "Active", value: "active" },
  { label: "Inactive", value: "inactive" },
  { label: "Pending", value: "pending" },
];

const softSkillVendorSchema = z.object({
  vendorName: z.string().min(1, "Vendor/Trainer Name is required"),
  contactPerson: z.string().min(1, "Contact Person is required"),
  email: z.string().email("Invalid email").min(1, "Email is required"),
  phone: z.string().regex(/^\d{10}$/, "Phone number must be exactly 10 digits"),
  expertise: z.array(z.string()).min(1, "Select at least one expertise"),
  status: z.enum(["active", "inactive", "pending"]),
  city: z.string().min(1, "City is required"),
  state: z.string().min(1, "State is required"),
  preferredDepartments: z.array(z.string()).optional(),
  rating: z.number().optional(),
  completedPrograms: z.number().optional(),
  coursesOffered: z.string().optional(),
  description: z.string().optional(),
});

type SoftSkillVendorFormType = z.infer<typeof softSkillVendorSchema>;

const SoftSkillVendorDialog = ({
  mode,
  isOpen,
  onOpenChange,
  filters,
  vendorId,
  vendorData,
  onCancel,
}: SoftSkillVendorDialogProps) => {
  const createMutation = useCreateSoftSkillVendorMutation(filters);
  const updateMutation = useUpdateSoftSkillVendorMutation(filters);
  const isPending = createMutation.isPending || updateMutation.isPending;

  const form = useForm<SoftSkillVendorFormType>({
    resolver: zodResolver(softSkillVendorSchema),
    defaultValues: {
      vendorName: "",
      contactPerson: "",
      email: "",
      phone: "",
      expertise: [],
      status: "active",
      city: "",
      state: "",
      preferredDepartments: [],
      rating: 0,
      completedPrograms: 0,
      coursesOffered: "",
      description: "",
    },
  });

  useEffect(() => {
    if (isOpen) {
      if (mode === "edit" && vendorData) {
        setTimeout(() => {
          form.reset({
            vendorName: vendorData.vendorName,
            contactPerson: vendorData.contactPerson,
            email: vendorData.email,
            phone: vendorData.phone || "",
            expertise: vendorData.expertise || [],
            status: vendorData.status,
            city: vendorData.city || "",
            state: vendorData.state || "",
            preferredDepartments: vendorData.preferredDepartments || [],
            rating: vendorData.rating || 0,
            completedPrograms: vendorData.completedPrograms || 0,
            coursesOffered: vendorData.coursesOffered || "",
            description: vendorData.description || "",
          });
        }, 0);
      } else {
        form.reset({
          vendorName: "",
          contactPerson: "",
          email: "",
          phone: "",
          expertise: [],
          status: "active",
          city: "",
          state: "",
          preferredDepartments: [],
          rating: 0,
          completedPrograms: 0,
          coursesOffered: "",
          description: "",
        });
      }
    }
  }, [mode, vendorData, isOpen, form]);

  const handleSubmit = (values: SoftSkillVendorFormType) => {
    const submissionData: SoftSkillVendorCreateType = {
      ...values,
      phone: values.phone || "",
      expertise: values.expertise || [],
      city: values.city || "",
      state: values.state || "",
      preferredDepartments: values.preferredDepartments || [],
      rating: values.rating || 0,
      completedPrograms: values.completedPrograms || 0,
      coursesOffered: values.coursesOffered || "",
      description: values.description || "",
    };

    if (mode === "create") {
      createMutation.mutate(submissionData, { onSuccess: onCancel });
    } else if (mode === "edit" && vendorId) {
      updateMutation.mutate(
        { _id: vendorId, data: submissionData },
        { onSuccess: onCancel }
      );
    }
  };

  const defaultExpertiseOptions =
    vendorData?.expertise?.map((e) => ({
      label: e,
      value: e,
    })) || [];

  const defaultDepartmentOptions =
    vendorData?.preferredDepartments?.map((id) => ({
      label: `Dept ${id}`, // Fallback if data not fully joined
      value: id,
    })) || [];

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-h-[90vh] overflow-y-auto sm:max-w-2xl"
        onInteractOutside={(e) => {
          e.preventDefault();
        }}
      >
        <DialogHeader>
          <DialogTitle>
            {mode === "create"
              ? "Add New Soft Skill Vendor"
              : "Edit Soft Skill Vendor"}
          </DialogTitle>
          <p className="text-muted-foreground mt-1 text-sm">
            Fill in the details to{" "}
            {mode === "create" ? "add a new" : "edit the"} soft skill training
            vendor
          </p>
        </DialogHeader>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(handleSubmit)}
            className="grid grid-cols-1 gap-4 py-2 md:grid-cols-2"
          >
            {/* Vendor/Trainer Name */}
            <InputField<SoftSkillVendorFormType>
              type="text"
              name="vendorName"
              label="Vendor/Trainer Name"
              placeholder=""
              required
              disabled={isPending}
            />

            {/* Contact Person */}
            <InputField<SoftSkillVendorFormType>
              type="text"
              name="contactPerson"
              label="Contact Person"
              placeholder=""
              required
              disabled={isPending}
            />

            {/* Email */}
            <InputField<SoftSkillVendorFormType>
              type="email"
              name="email"
              label="Email"
              placeholder=""
              required
              disabled={isPending}
            />

            {/* Phone Number */}
            <InputField<SoftSkillVendorFormType>
              type="tel"
              name="phone"
              label="Phone Number"
              placeholder=""
              required
              disabled={isPending}
            />

            {/* Expertise */}
            <AsyncMultiSelectField<SoftSkillVendorFormType>
              name="expertise"
              label="Expertise"
              placeholder="Select expertise"
              fetchOptions={softSkillVendorService.fetchExpertiseOptions}
              queryKey={["softSkillExpertise"]}
              defaultOptions={defaultExpertiseOptions}
              required
              disabled={isPending}
            />

            {/* Status */}
            <SelectField<SoftSkillVendorFormType>
              name="status"
              label="Status"
              options={STATUS_OPTIONS}
              required
              disabled={isPending}
            />

            {/* City */}
            <InputField<SoftSkillVendorFormType>
              type="text"
              name="city"
              label="City"
              placeholder="e.g., Bangalore"
              required
              disabled={isPending}
            />

            {/* State */}
            <InputField<SoftSkillVendorFormType>
              type="text"
              name="state"
              label="State"
              placeholder="e.g., Karnataka"
              required
              disabled={isPending}
            />

            {/* Preferred Departments */}
            <AsyncMultiSelectField<SoftSkillVendorFormType>
              name="preferredDepartments"
              label="Preferred Departments"
              placeholder="Select preferred departments"
              fetchOptions={departmentService.fetchDepartmentOptions}
              queryKey={["departments"]}
              defaultOptions={defaultDepartmentOptions}
              disabled={isPending}
              className={{ itemClass: "md:col-span-2" }}
            />

            {/* Rating */}
            <InputField<SoftSkillVendorFormType>
              type="number"
              name="rating"
              label="Rating"
              placeholder="e.g., 4.5"
              step="0.1"
              disabled={isPending}
            />

            {/* Completed Programs */}
            <InputField<SoftSkillVendorFormType>
              type="number"
              name="completedPrograms"
              label="Completed Programs"
              placeholder="e.g., 10"
              disabled={isPending}
            />

            {/* Courses Offered */}
            <TextareaField<SoftSkillVendorFormType>
              name="coursesOffered"
              label="Courses Offered"
              placeholder="List of courses or training programs offered"
              disabled={isPending}
              className={{ itemClass: "md:col-span-2" }}
            />

            {/* Description */}
            <TextareaField<SoftSkillVendorFormType>
              name="description"
              label="Description"
              placeholder="Brief description about the vendor/trainer"
              disabled={isPending}
              className={{ itemClass: "md:col-span-2" }}
            />

            <DialogFooter className="md:col-span-2">
              <Button
                type="button"
                variant="outline"
                onClick={onCancel}
                disabled={isPending}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isPending}>
                {isPending
                  ? mode === "create"
                    ? "Adding..."
                    : "Saving..."
                  : mode === "create"
                    ? "Create Vendor"
                    : "Save Changes"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default SoftSkillVendorDialog;
